// integrations/third-party-embeds.js

/**
 * Re-initialise third-party embed widgets (Elfsight, Instagram, etc.) after
 * Barba.js transitions.
 *
 * Embed platforms load a loader script that scans the DOM once and mounts each
 * widget placeholder it finds. When Barba swaps the page container via AJAX, a
 * fresh (empty) placeholder is inserted but the already resident loader does not
 * reliably re-scan the rebuilt subtree — so the widget stays blank on return
 * navigations. This module re-injects the relevant loader script, which re-runs
 * the scan and mounts any placeholder that is not yet hydrated.
 *
 * The embed list (placeholder selector + loader src) is nominated in PHP via
 * esr_get_third_party_embeds() and passed through as app.settings.thirdPartyEmbeds.
 * Each loader is also expected to be enqueued globally by the theme so its
 * MutationObserver is always alive; this only handles the re-scan nudge.
 */

/**
 * Read the localized embed config injected by the theme.
 * @return {Array<{selector: string, src: string}>} Configured embeds.
 */
function getEmbedConfig() {
  const embeds = window.app && window.app.settings && window.app.settings.thirdPartyEmbeds;
  return Array.isArray(embeds) ? embeds : [];
}

/**
 * Determine whether an embed placeholder has already been hydrated.
 * A platform renders content (an iframe / shadow host / markup) inside the
 * placeholder once mounted, so an empty placeholder is still awaiting hydration.
 * @param {Element} el - The placeholder element.
 * @return {boolean} True if the widget still needs mounting.
 */
function isUnhydrated(el) {
  return el.children.length === 0 && !el.shadowRoot;
}

/**
 * Re-inject a loader script to force its platform to re-scan the document and
 * mount any placeholders that are not yet hydrated. Re-adding the script element
 * causes it to execute again; already-mounted widgets are skipped by the loader.
 * @param {string} src - The loader script URL to re-inject.
 * @return {void}
 */
function reinjectLoader(src) {
  document
    .querySelectorAll(`script[src^="${src}"]`)
    .forEach((s) => s.remove());

  const script = document.createElement('script');
  script.src = src;
  script.defer = true;
  script.setAttribute('data-embed-reinit', '');
  document.body.appendChild(script);
}

/**
 * Re-initialise any configured third-party embed widgets within the container.
 * @param {Element|Document} [container=document] - Scope to check for widgets.
 * @return {void}
 */
export function initializeThirdPartyEmbeds(container = document) {
  const scope = container || document;
  const embeds = getEmbedConfig();
  if (!embeds.length) return;

  embeds.forEach(({ selector, src }) => {
    if (!selector || !src) return;

    const widgets = scope.querySelectorAll(selector);
    if (!widgets.length) return;

    const needsMount = Array.from(widgets).some(isUnhydrated);
    if (needsMount) reinjectLoader(src);
  });
}
